import { useEffect } from "react";
import {
  BOT_AVATAR_FILL,
  botAvatarLabel,
  type BotAvatarColor,
  type BotAvatarShape,
} from "./botIdentity";
import type { BoardLegendItem } from "./legendTypes";
import { useOverlayScrollLock } from "./useOverlayScrollLock";
import { useBoardWorkflow } from "./useBoardWorkflow";
import type { WorkflowBot } from "./workflowFallback";

export function WorkflowDrawer({
  item,
  onClose,
}: {
  item: BoardLegendItem;
  onClose: () => void;
}) {
  useOverlayScrollLock(true);
  const { settled, templateFor } = useBoardWorkflow();
  const template = templateFor(item.token);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-[rgba(20,24,28,0.55)]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="founder-workflow-title"
        data-workflow-source={template?.source ?? "fallback"}
        className="fixed top-1/2 left-1/2 z-10 flex max-h-[min(86vh,720px)] w-[min(720px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-[#e6e6e4] bg-white text-card-foreground shadow-md max-md:top-auto max-md:right-0 max-md:bottom-0 max-md:left-0 max-md:max-h-[80vh] max-md:w-full max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-t-[12px] max-md:rounded-b-none dark:border-border dark:bg-card"
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#e6e6e4] px-4 pt-4 pb-3 dark:border-border">
          <div className="min-w-0">
            <h3
              id="founder-workflow-title"
              className="text-[17px] leading-snug font-semibold"
            >
              流程 · {item.label}
            </h3>
            <p className="mt-1 text-[12px] text-muted-foreground">
              {template ? `${template.name} · ${template.key}` : item.token}
            </p>
          </div>
          <button
            type="button"
            className="size-8 shrink-0 rounded-md border border-[#e6e6e4] bg-white text-muted-foreground hover:bg-[#f3f3f1] dark:border-border dark:bg-card"
            aria-label="关闭弹层"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="flex-1 space-y-3 overflow-auto px-4 py-3 text-[13px]">
          {settled && template?.source === "fallback" ? (
            <p className="rounded-md bg-[#f7f7f5] px-3 py-2 text-[12px] text-muted-foreground dark:bg-muted/40">
              库还读不到这条流程，这是静态包。
            </p>
          ) : null}
          {!template ? (
            <p>这条线还没有流程模板。</p>
          ) : template.nodes.length === 0 ? (
            <p>空模板，没有节点。不开产。</p>
          ) : (
            <ol aria-label={`${template.name}节点`} className="flex flex-col">
              {template.nodes.map((node, index) => (
                <li key={node.node_key} className="flex gap-3">
                  <span className="flex w-6 shrink-0 flex-col items-center">
                    <span className="flex size-6 items-center justify-center rounded-full border border-[#e6e6e4] text-[12px] font-medium text-[#1a4a7a] dark:border-border dark:text-[#c5d9ef]">
                      {index + 1}
                    </span>
                    {index < template.nodes.length - 1 ? (
                      <span
                        data-flow-edge=""
                        aria-hidden
                        className="my-1 w-px flex-1 bg-[#c8c8c6] dark:bg-border"
                      />
                    ) : null}
                  </span>
                  <div className="mb-3 min-w-0 flex-1 rounded-md border border-[#e6e6e4] px-2 py-2 dark:border-border">
                    <span className="font-medium">{node.label}</span>
                    {node.is_blind_review ? (
                      <span className="mt-1 block text-[11px] text-[#1a4a7a] dark:text-[#c5d9ef]">
                        盲审
                      </span>
                    ) : null}
                    {node.bots.length ? (
                      <span className="mt-1 flex flex-col gap-1">
                        {node.bots.map((entry) => (
                          <WorkflowBotLine
                            key={`${entry.agent_id}-${entry.bot_role}`}
                            entry={entry}
                          />
                        ))}
                      </span>
                    ) : (
                      <span className="mt-1 block text-[11px] text-muted-foreground">
                        在岗 Bot：未挂
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
        <div className="flex justify-end border-t border-[#e6e6e4] bg-[#f7f7f5] px-4 py-3 dark:border-border dark:bg-muted/40">
          <button
            type="button"
            className="rounded-md border border-[#e6e6e4] bg-white px-3 py-1 text-[13px] font-medium text-[#1a4a7a] hover:bg-[#f3f3f1] dark:border-border dark:bg-card dark:text-[#c5d9ef]"
            onClick={onClose}
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}

export function WorkflowBotLine({ entry }: { entry: WorkflowBot }) {
  const identity = entry.identity;
  const name = identity?.display_name || entry.role_name;
  return (
    <span
      className="flex items-start gap-1 text-[11px] leading-snug text-muted-foreground"
      data-agent-id={entry.agent_id}
    >
      {identity?.avatar_color ? (
        <BotAvatar
          name={name}
          color={identity.avatar_color}
          shape={identity.avatar_shape}
        />
      ) : null}
      <span className="min-w-0">
        <span className="block">{name}</span>
        {identity?.tag ? (
          <span className="mt-0.5 inline-block rounded-sm bg-[#f3f3f1] px-1 text-[10px] leading-4 dark:bg-muted">
            {identity.tag}
          </span>
        ) : null}
        {identity ? null : <span className="block text-[10px]">未映射</span>}
      </span>
    </span>
  );
}

function BotAvatar({
  name,
  color,
  shape,
}: {
  name: string;
  color: BotAvatarColor;
  shape: BotAvatarShape | null;
}) {
  const fill = BOT_AVATAR_FILL[color];
  const label = botAvatarLabel(name, color, shape);
  return (
    <svg
      role="img"
      aria-label={label}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      className="mt-0.5 shrink-0"
    >
      {shape === "cylinder" ? (
        <>
          <ellipse cx="8" cy="3.4" rx="4.2" ry="1.7" fill={fill} />
          <path
            d="M3.8 3.6h8.4v8.2c0 1.2-1.9 2.1-4.2 2.1s-4.2-.9-4.2-2.1V3.6z"
            fill={fill}
          />
          <ellipse
            cx="8"
            cy="3.4"
            rx="4.2"
            ry="1.7"
            fill={fill}
            opacity="0.75"
          />
        </>
      ) : shape === "crystal" ? (
        <polygon points="8,1 14.5,8 8,15 1.5,8" fill={fill} />
      ) : shape === "leaf" ? (
        <path
          d="M8 1.5c2.2 3 6 4.6 6 8.2a6 6 0 0 1-12 0C2 6.1 5.8 4.5 8 1.5z"
          fill={fill}
        />
      ) : shape === "gem" ? (
        <polygon points="3.5,2 12.5,2 15,6 8,15 1,6" fill={fill} />
      ) : shape === "pebble" ? (
        <ellipse cx="8" cy="9" rx="5.4" ry="4" fill={fill} />
      ) : (
        <rect x="3.5" y="3.5" width="9" height="9" fill={fill} />
      )}
    </svg>
  );
}
